import apiClient from './apiClient';

export interface TeamMember {
  id: string;
  name: string;
  timezone: string;
  schedule: Record<number, number[]>;
  createdAt: string;
  updatedAt: string;
}

export interface CreateTeamMemberRequest {
  name: string;
  timezone: string;
}

export interface UpdateTeamMemberRequest {
  name?: string;
  timezone?: string;
}

export interface UpdateScheduleRequest {
  schedule: Record<number, number[]>;
}

export interface TeamMemberStatus {
  memberId: string;
  name: string;
  isOnline: boolean;
  nextOnline: string | null;
}

export interface TimetableSettings {
  id: string;
  ownerId: string;
  discordWebhookEnabled: boolean;
  discordWebhookUrl: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface UpdateTimetableSettingsRequest {
  discordWebhookEnabled: boolean;
  discordWebhookUrl?: string;
}

export const timetablesService = {
  // Team Member operations
  async createTeamMember(data: CreateTeamMemberRequest): Promise<TeamMember> {
    const response = await apiClient.post('/timetables/team-members', data);
    return response.data;
  },

  async getTeamMembers(): Promise<TeamMember[]> {
    const response = await apiClient.get('/timetables/team-members');
    return response.data;
  },

  async getTeamMember(id: string): Promise<TeamMember> {
    const response = await apiClient.get(`/timetables/team-members/${id}`);
    return response.data;
  },

  async updateTeamMember(id: string, data: UpdateTeamMemberRequest): Promise<TeamMember> {
    const response = await apiClient.put(`/timetables/team-members/${id}`, data);
    return response.data;
  },

  async deleteTeamMember(id: string): Promise<void> {
    await apiClient.delete(`/timetables/team-members/${id}`);
  },

  // Schedule operations
  async updateSchedule(id: string, data: UpdateScheduleRequest): Promise<TeamMember> {
    const response = await apiClient.put(`/timetables/team-members/${id}/schedule`, data);
    return response.data;
  },

  // Status operations
  async getTeamMemberStatuses(): Promise<TeamMemberStatus[]> {
    const response = await apiClient.get('/timetables/statuses');
    return response.data;
  },

  // Settings operations
  async getSettings(): Promise<TimetableSettings> {
    const response = await apiClient.get('/timetables/settings');
    return response.data;
  },

  async updateSettings(data: UpdateTimetableSettingsRequest): Promise<TimetableSettings> {
    const response = await apiClient.put('/timetables/settings', data);
    return response.data;
  },

  async sendTestWebhook(): Promise<{ success: boolean; message: string }> {
    const response = await apiClient.post('/timetables/settings/test-webhook');
    return response.data;
  },
};
